<template>
    <!-- Div to help align form -->
    <div class="align-center-helper">
        <!-- *********************************************
        *              Origin Account Block              *
        ********************************************** -->
        <divider-generic
        icon="account-balance"
        :title="t('cards.batchfile.newCharge.step2.originAccountTitle')"
        :applyBorder="false"/>
        <!-- Account Dropdown -->
        <dropdown-account
        :params="{ labelText: t('cards.batchfile.newCharge.step2.account'), mandatory: true }"
        :options="getAccounts"
        :selectedOption="formData.originAccount"
        @clicked="emit('updateFormData', { field: 'originAccount', value: $event })"/>
        <!-- *********************************************
        *                Information Block               *
        ********************************************** -->
        <divider-generic icon="info" :title="t('cards.batchfile.newCharge.step2.infoTitle')"/>
        <!-- File Description -->
        <input-generic
        id="fileDescription"
        :params="fileDescriptionParams"
        :inputValue="formData.fileDescription"
        @onBlur="fileDescriptionValidation"
        @input="[emit('updateFormData', { field: 'fileDescription', value: $event }), clearFieldError('fileDescriptionParams')]"/>
        <!-- Email -->
        <input-generic
        id="email"
        :params="emailParams"
        :inputValue="formData.email"
        @onBlur="emailValidation"
        @input="[emit('updateFormData', { field: 'email', value: $event.trim() }), clearFieldError('emailParams')]"/>
        <!-- Available Plafond -->
        <text-block-generic
        v-if="getCardGroupPlafond && getCardGroupPlafond !== -1"
        :params="{
            label: t('cards.batchfile.newCharge.step2.plafond'),
            content: amountFormat(getCardGroupPlafond, ACCOUNT_MAIN_CURRENCY)
        }"/>
        <!-- *********************************************
        *                Charge List Block               *
        ********************************************** -->
        <divider-generic icon="card-recharge" :title="t('cards.batchfile.newCharge.step2.chargeListTitle')"/>
    </div>
    <!-- Table -->
    <table-with-horizontal-scroll
    id="table-with-horizontal-scroll"
    rowHeightClass="medium"
    :hasDetail="false"
    :headerData="tableHeaderData"
    :tableStyles="tableStyles"
    :tableData="tableData"
    @dropdownClicked="emit('updateBatchfileEntry', { ...$event, field: $event.dropdownIdentifier, value: $event.selectedOption })"
    @inputChanged="emit('updateBatchfileEntry', { ...$event, field: $event.inputIdentifier, value: $event.inputValue })"
    @onBlur="emit('tableInputBlurHandler', $event)"
    @buttonClicked="emit('removeRow', $event.rowId)"/>
    <!-- Button to Add more lines -->
    <div v-if="batchfileEntries.length < getCards.length" class="button-align-helper">
        <button-secondary
        icon="plus"
        buttonSize="small"
        :label="t('cards.batchfile.newCharge.step2.addLine')"
        @clicked="emit('addNewLine')"/>
    </div>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import InputGeneric from '@/components/generic/inputs/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import TableWithHorizontalScroll from '@/components/generic/table/WithHorizontalScroll'
import ButtonSecondary from '@/components/generic/buttons/Secondary'

/* Vue & Assets */
import { ref, watch, onMounted, computed } from 'vue'
import { removeAccents, noSpecialChar, emailValidator } from '@/assets/scripts/validators'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'
import { amountFormat } from '@/assets/scripts/amounts'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateFormData', 'updateBatchfileEntry', 'tableInputBlurHandler', 'formErrorsCleared', 'resetShowMandatoryErrorsProp', 'removeRow', 'addNewLine', 'scrollTo'])

/** Props
@formData {Object} Object with info for our current form
@batchfileEntries {Array} Array of charge entries
@mandatoryList { Array } Array of mandatory keys corresponding with our form
@invalidRowsData {Boolean} prop with boolean to tell us if any row has errors
@showMandatoryErrors { Boolean } Prop to trigger display of mandatory errors
@clearFormErrors { Boolean } - Prop to remove of form errors
*/
const props = defineProps({
	formData: Object,
    batchfileEntries: Array,
	mandatoryList: Array,
    invalidRowsData: Boolean,
	showMandatoryErrors: Boolean,
	clearFormErrors: Boolean
})

const { getCards, getAccounts, getCardGroupPlafond } = mapGetters(moduleMapper.CARDS_BATCHFILE_NEW_CHARGE)

const fileDescriptionParams = ref({
    labelText: t('cards.batchfile.newCharge.step2.fileDescription'),
	placeholder: t('cards.batchfile.newCharge.step2.fileDescriptionPlaceholder'),
    bottomMessage: t('cards.batchfile.newCharge.step2.fileDescriptionBottomMessage'),
    ourMask: {
        validatorFn: value => { return noSpecialChar(value) },
        parseInputText: value => { return removeAccents(value) }
    },
	mandatory: true,
	maxLength: 30,
	errorState: false,
	errorMessage: null
})

const emailParams = ref({
    labelText: t('cards.batchfile.newCharge.step2.email'),
	placeholder: t('cards.batchfile.newCharge.step2.emailPlaceholder'),
    bottomMessage: t('cards.batchfile.newCharge.step2.emailBottomMessage'),
	mandatory: true,
	maxLength: 50,
	errorState: false,
	errorMessage: null
})

const emailValidation = () => {
	if (props.formData.email.length && !emailValidator(props.formData.email)) {
		emailParams.value.errorState = true
		emailParams.value.errorMessage = t('invalidEmailError')
	}
}

const currency = computed(() => {
    return getAccounts.value.find(obj => obj.id === props.formData.originAccount).currency
})

const tableHeaderData = [
	{ label: t('cards.batchfile.newCharge.step2.card'), dataAttr: 'cardId', mandatory: true },
    { label: t('cards.batchfile.newCharge.step2.amount'), dataAttr: 'amount', mandatory: true },
    { label: t('cards.batchfile.newCharge.step2.description'), dataAttr: 'description' },
    { dataAttr: 'closeRow', rowCellType: 'button' }
]

const tableStyles = [
    { width: '372px', 'min-width': '372px' },
    { width: '300px', 'min-width': '300px' },
    { width: '300px', 'min-width': '300px' },
    { padding: '0 0 14px 0' }
]

const selectedCards = computed(() => {
    return props.batchfileEntries.reduce((acc, entry) => {
        if (entry.cardId.value && entry.cardId.value.length) acc.push(entry.cardId.value)
        return acc
    }, [])
})

const getAvailableCards = (entryRowCardId) => {
    if (!selectedCards.value.length) return getCards.value

    return getCards.value.filter(obj => {
        if (obj.id === entryRowCardId) return true
        if (!selectedCards.value.find(card => obj.id === card)) return true
        return false
    })
}

const tableData = computed(() => {
    return props.batchfileEntries.map((entry, index) => {
        return [
            {
                value: index,
                dataAttr: 'id',
                errorState: entry.mdwErrorCode,
                errorMessage: entry.mdwErrorMessage
            },
            {
                type: 'dropdown',
                dataAttr: 'cardId',
                value: {
                    type: 'card',
                    dropdownParams: {
                        headerType: 'no-card-preview',
                        withBorder: true,
                        errorState: entry.cardId.errorState,
                        errorMessage: t('mandatoryFieldError'),
                        placeholder: t('cards.batchfile.newCharge.step2.cardPlaceholder')
                    },
                    selectedOption: entry.cardId.value || '',
                    options: getAvailableCards(entry.cardId?.value).map(obj => {
                        return {
                            ...obj,
                            rowType: 'no-card-preview'
                        }
                    })
                }
            },
            {
                type: 'input',
                dataAttr: 'amount',
                value: {
                    type: 'amount',
                    inputParams: {
                        currency: currency.value || '',
                        errorState: entry.amount.errorState,
                        errorMessage: entry.amount.value && Number(entry.amount.value) === 0 ? t('invalidAmount') : t('mandatoryFieldError')
                    },
                    inputValue: entry.amount ? entry.amount.value : ''
                }
            },
            {
                type: 'input',
                dataAttr: 'description',
                value: {
                    type: 'generic',
                    inputParams: {
                        errorState: entry.description.errorState,
                        errorMessage: t('mandatoryFieldError'),
                        bottomMessage: t('cards.batchfile.newCharge.step2.descriptionBottomMessage'),
                        placeholder: t('cards.batchfile.newCharge.step2.descriptionPlaceholder'),
                        ourMask: {
                            validatorFn: value => { return noSpecialChar(value) },
                            parseInputText: value => { return removeAccents(value) }
                        },
                        maxValue: 30
                    },
                    inputValue: entry.description.value
                }
            },
            { type: 'button', value: { iconName: 'close', variant: 'tertiary', disabled: props.batchfileEntries.length === 1 }, dataAttr: 'closeRow' }
        ]
    })
})

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	fileDescriptionParams: fileDescriptionParams.value,
	emailParams: emailParams.value
})

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
	emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

	if (mandatoryFieldsEmptyList.length) {
		/* Scroll to radio field if theres an error there, instead of scrolling to the empty mandatory field after it */
		emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

		mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
		})
	} else if (props.invalidRowsData) {
        /* Scroll to radio field if theres an error there, instead of scrolling to the empty mandatory field after it */
		emit('scrollTo', getElementOffsetTopPosition('table-with-horizontal-scroll'))
    }
}
/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
	clearFormErrors()
	emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
	allFormFields.value[fieldName].errorMessage = null
	allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in allFormFields.value) {
		clearFieldError(fieldName)
	}
}

onMounted(() => { emit('scrollTo', 0) })
</script>

<style lang="scss" scoped>
.align-center-helper {
    width: 508px;
    max-width: 100%;

    display: flex;
    flex-direction: column;
    gap: 24px;
}

.button-align-helper {
    width: 100%;
}
</style>
